/**
 * Legacy bridge — first-entry (empty graph) domain.
 *
 * Owns access to the EJS-era globals the first-entry flow needs:
 * `userFactory` (current app / graph options / empty flag),
 * `analyticsPanel`, `messageFactory.showHelperTop`, and
 * `graphShareConstructor.saveGraphVersion`.
 *
 * Siblings: `views/react/components/LeftMenuMigration/legacyBridge.js`
 * (cross-cutting helpers — `getCurrentContext`, `getContextOption`,
 * `emitEvent` are imported from there, not duplicated here) and
 * `views/react/components/ProjectNotes/legacyBridge.js`.
 */

function getUserFactory() {
    if (typeof window === 'undefined') return null
    if (typeof window.userFactory === 'undefined') return null
    return window.userFactory
}

/**
 * Whether the page was server-rendered with no entries in the context.
 * Mirrors `userFactory.getGraphEmpty()`, which `contextSettings.ejs` sets
 * from `entries.length == 0` at render time. This is the same data the
 * removed FirstEntryLogic re-fetched via `getJsonPath()` to double-check
 * (its own TODO marked that fetch unnecessary), so the flag alone is
 * authoritative for the initial load.
 */
export function isGraphEmptyOnLoad() {
    const uf = getUserFactory()
    return !!(uf && uf.getGraphEmpty && uf.getGraphEmpty() === true)
}

/**
 * The current "app" — the third URL segment (`/:user/:context/:app`),
 * server-assigned via `userFactory.setCurrentApp(pathapp)`. Values seen
 * by the first-entry flow: 'seo', 'kwrds', 'niche', 'google', 'notes',
 * 'surveys', 'reviews', 'twitter', 'twitter_social', 'mindmap',
 * 'findall', 'edit', …
 */
export function getCurrentApp() {
    const uf = getUserFactory()
    if (uf && uf.getCurrentApp) return uf.getCurrentApp() || null
    return null
}

/**
 * Read a graph option (`mode`, `chat`, `editor`, `speech`, …) — these are
 * seeded into userFactory from the URL query by `contextSettings.ejs`.
 */
export function getGraphOption(name) {
    const uf = getUserFactory()
    if (uf && uf.getGraphOption) return uf.getGraphOption(name)
    return null
}

/** Soft-hide the legacy analytics pane (same as a user toggling it away). */
export function hideAnalyticsPanel() {
    if (typeof window === 'undefined') return
    window.analyticsPanel?.hidePanel?.()
}

/** Hard-hide the legacy analytics pane (used on fully empty graphs). */
export function forceHideAnalyticsPanel() {
    if (typeof window === 'undefined') return
    window.analyticsPanel?.forceHide?.()
}

/**
 * Show the dismissible helper bar at the top of the page.
 * `type` is the legacy "don't show this again" key (e.g. 'seo_helper').
 */
export function showHelperTop(message, timeout, type) {
    if (typeof window === 'undefined') return
    window.messageFactory?.showHelperTop?.(message, timeout, type)
}

/**
 * Ask the statements editor to open (legacy `forceOpenStatements()`).
 * `MyEventEmitter.emit` THROWS when an event has no listeners, and the
 * React listeners (`StatementEditor`, `WorkflowStepper`) mount after this
 * controller's effect runs — so guard on `_events` exactly like the
 * removed EJS helper did. When no listener exists yet the call is a no-op,
 * which matches the legacy behavior.
 */
export function forceOpenStatements() {
    if (typeof window === 'undefined') return
    const emitter = window.myEventEmitter
    if (emitter && emitter._events && emitter._events['onStatementsAction']) {
        emitter.emit('onStatementsAction', {
            action: 'force_open_statements',
        })
    }
}

/**
 * Persist a default graph version (revision URL) via the legacy share
 * constructor. `versionUrlParams` is the query-string payload
 * `graphShareConstructor.saveGraphVersion` expects; the second arg
 * suppresses the "version saved" user message.
 */
export function saveDefaultGraphVersion(versionUrlParams) {
    if (typeof window === 'undefined') return
    const ctor = window.graphShareConstructor
    if (ctor && typeof ctor.saveGraphVersion === 'function') {
        ctor.saveGraphVersion(versionUrlParams, true)
    }
}
